import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { Hero } from '../../../ui/hero/Index';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const FritidsArtikkel: React.FC<RouteComponentProps<IRoute>> = ({
  location
}) => {
  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  return (
    <>
      <Seo
        title="Høy lokalkunnskap er viktig for kjøperen - PrivatMegleren"
        description="Stegene man går gjennom for å selge en fritidseiendom på fjellet
        eller ved sjøen har flere likhetstrekk med salg av annen eiendom.
        Sammenlignet med et eiendomssalg er det imidlertid store forskjeller
        ved prosessen kjøperen går gjennom."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Toppbilde_1.jpg"
        opacity={0.4}
        className="content-height flex padding-top-50"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Høy lokalkunnskap er viktig for kjøperen
          </Heading>
          <Paragraph center={true}>
            Stegene man går gjennom for å selge en fritidseiendom på fjellet
            eller ved sjøen har flere likhetstrekk med salg av annen eiendom.
            Sammenlignet med et eiendomssalg er det imidlertid store forskjeller
            ved prosessen kjøperen går gjennom. Forskjeller det er viktig å vite
            om når du skal velge megler.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container style={pageFadeIn}>
        <Section>
          <Paragraph center={true}>
            Ved salg av fritidseiendom er det en avgjørende suksessfaktor at
            megleren kjenner området like godt som en lokalkjent. Det er nemlig
            ikke uvanlig at de som kommer på visning ikke er så godt kjent i
            området, og derfor er avhengig av å få kunnskap av noen som kjenner
            området godt. Eiendomsmegler Pia M. Murland i PrivatMegleren
            Lillehammer forteller at potensielle kjøpere ikke bare er
            interessert i hytta de ser på visning, men også nærområdet.
          </Paragraph>
          <Paragraph center={true}>
            – Som megler må jeg være oppdatert på alt som skjer i nærområdet.
            Det handler om å kjenne til alle fordelene og mulighetene området
            har å by på, slik at kjøper kan bli godt kjent med nærmiljøet på
            kort tid. Interesseområdet for potensielle kjøpere av en hytte på
            fjellet er bredt og spenner over alt fra turmuligheter, fiskevann og
            badesteder, til arrangementer, samlinger og after-ski, forklarer
            eiendomsmegler Pia.
          </Paragraph>
          <Heading tag="h3" center={true}>
            Byr på detaljene
          </Heading>

          <Paragraph center={true}>
            Pia sammenligner jobben som megler med å være en ambassadør for
            området. Ofte er hun innom de lokale kafeene eller slår av en prat
            med brøytebilsjåførene for å få siste nytt. Kunnskapen hun tilegner
            seg om nærområdet er ofte på et forbausende detaljnivå.
          </Paragraph>
          <Paragraph center={true}>
            Eiendomsmegler Gustav J. Sædberg i PrivatMegleren Sædberg og Lian
            kjenner seg godt igjen i Pias beskrivelse. Selv om
            fritidseiendommene han er megler for som regel ligger ved sjøen,
            forteller han at lokalkunnskapen er like viktig i skjærgården.
          </Paragraph>
          <Paragraph center={true}>
            – Også ved sjøen finnes det spesielle steder – gode badeplasser,
            fiskesteder og hvor du kan være sikker på å finne krabber. Siden vi
            ofte er avhengig av båt for å komme ut til eiendommene tar vi oss
            gjerne tid til å vise frem det som finnes nærområdet. Som megler må
            man også tørre å by på seg selv, og fortelle hvordan man selv bruker
            skjærgården, forteller Gustav.
          </Paragraph>
          <ImageContainer>
            <img
              style={{ width: '100%' }}
              src="https://cdn.reeltime.no/pm_assets/kampanjer/leveranse/fritid/Artikkelbilde_1.jpg"
              alt="artikkel bilde"
              className="artikkelbilde_1"
            />
          </ImageContainer>
          <Heading tag="h3" center={true}>
            Lokalkunnskap som spisskompetanse
          </Heading>
          <Paragraph center={true}>
            Gustav peker imidlertid på at byggeforbudet i strandsonen trekker et
            viktig skille mellom fjell og sjø. Forbudet innebærer at det ikke
            bygges nye fritidseiendommer nærmere enn 100 meter fra sjøen.
          </Paragraph>
          <Paragraph center={true}>
            – En del av vår lokalkunnskap er også å vite hva som er tillatt å
            gjøre på eiendommen. Derfor har vi spisskompetanse på regelverk og
            søknadsprosesser i den enkelte kommune, samt hvilke endringer man
            kan få tillatelse til å gjøre, forklarer Gustav.
          </Paragraph>
          <Heading tag="h3" center={true}>
            Nærområdet er avgjørende
          </Heading>
          <Paragraph center={true}>
            Både Pia og Gustav forteller at de som vurderer å kjøpe en
            fritidseiendom gjerne ser på færre eiendommer enn ved for eksempel
            et eiendomskjøp.
          </Paragraph>
          <Paragraph center={true}>
            – Det er gjerne større følelser knyttet til kjøp av fritidseiendom
            enn ved kjøp av eiendom. Kjøperne «tar og føler» mer på eiendommen,
            og spør gjerne mer om nærområdet enn om selve hytta. Da er det
            avgjørende at megler både har god lokalkunnskap og får formidlet
            alle fordelene ved området, sier Gustav.
          </Paragraph>
          <Paragraph center={true}>
            På visningen benytter PrivatMegleren romplakater for å trekke frem
            nyttig informasjon om en eiendom, slik som takhøyde, kjøkkenfabrikat
            eller fortelle at et rom har gulvvarme. Pia forteller at
            romplakatene også aktivt brukes til å fortelle om nærområdet.
          </Paragraph>
          <Paragraph center={true}>
            – På romplakatene kan vi for eksempel vise kart over skiløyper og
            turstier i området, eller kanskje åpningstidene til alpinbakken og
            den lokale butikken. Slik blir det lettere for kjøperne å orientere
            seg og bli kjent med nærmiljøet, forklarer Pia, mens Gustav legger
            til:
          </Paragraph>
          <Paragraph center={true}>
            – For hytter ved sjøen kan vi vise vei til en fin badestrand på
            naboøya, eller gi praktiske opplysninger om hvor det er
            parkeringsplasser på fastlandet. Enten hytta ligger på fjellet eller
            ved sjøen er romplakatene et fint virkemiddel for å fremheve nyttig
            informasjon om de nære tingene som kjøperne er opptatte av, utdyper
            Gustav.
          </Paragraph>
        </Section>
        <SelgeForm />
        {/*  <KjopeForm /> */}
      </Container>
    </>
  );
};

const FormWrapper = styled(animated.div)``;
const SelgeForm: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { submitWithDigger } = useDigtectiveContext();

  const [lead, { error, loading }] = useMutation(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.valuation?.success) {
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Postnummer *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Section className="form-section" style={{ paddingBottom: '10px' }}>
          <Heading tag="h2" center={true}>
            Bli kontaktet
          </Heading>
          <Paragraph center={true} className="form-text">
            Ønsker du å vite mer om hvordan vi kan &nbsp; hjelpe deg?
          </Paragraph>
          <Paragraph center={true} className="form-text">
            Fyll ut skjemaet og en av våre meglere i ditt nærområde kontakt.
          </Paragraph>
        </Section>
        <Form
          onSubmit={handleSubmit}
          noValidate
          style={{ paddingBottom: '50px' }}
        >
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields.map((item, index) => {
            return (
              <FormRow key={`field_${index}`}>
                <Input
                  type={item?.type}
                  name={item?.name}
                  label={item?.label}
                  labelAfter={item?.labelAfter}
                  placeholder={item?.placeholder}
                  value={item?.value}
                  onChange={handleChange}
                  error={item?.error}
                />
              </FormRow>
            );
          })}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href={routeConfig.personvern.url}
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h2" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};

export default FritidsArtikkel;

const HeroContent = styled(animated.div)`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  max-width: 960px;
  padding: 0 30px;

  h1 {
    margin-bottom: 0;
    width: 100%;
    word-break: break-word;
  }

  h2 {
    width: 100%;
    word-break: break-word;
  }
`;

const ImageContainer = styled.div`
  img  {
    object-fit: contain;
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 250px;
  }

  @media all and (min-width: 600px) {
    img {
      object-fit: contain;
      display: block;
      margin-left: auto;
      margin-right: auto;
      width: 50%;
      padding: 1em 0 1em;
    }
  }
`;

/* const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;
  margin-bottom: 3em;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  .video-mobil {
    display: block;
  }
  .video-desktop {
    display: none;
  }

  @media (min-width: 600px) {
    .video-mobil {
      display: none;
    }
    .video-desktop {
      display: block;
    }
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`; */
